import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink, MapPin, BadgeCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");

export default function ProfileDirectory() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [entries, setEntries] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.directory(q, category);
      setEntries(r.entries || []);
      if (!category) setCats(r.categories || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [q, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6" data-testid="directory-tab">
      <div>
        <div className="mono text-kinetic">DIRECTORY · FILMMAKERS · CREATORS · ARTISTS · INFLUENCERS</div>
        <p className="text-white/55 mt-2 max-w-2xl">Browse everyone on INDIEWOOD. Investors, brands, producers and viewers can discover talent, open a profile and reach out for work.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, skill, role, location…" className="bg-black border-white/15 rounded-sm pl-9" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="dir-cat-all" onClick={() => setCategory("")}
          className={`mono px-3 h-8 rounded-sm border ${category === "" ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>ALL</button>
        {cats.map(c => (
          <button key={c} data-testid={`dir-cat-${c}`} onClick={() => setCategory(c)}
            className={`mono px-3 h-8 rounded-sm border ${category === c ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>{c.toUpperCase()}</button>
        ))}
      </div>

      {loading && <div className="mono text-white/40 py-10 text-center">LOADING…</div>}
      {!loading && entries.length === 0 && (
        <div className="border border-dashed border-white/15 rounded-lg p-12 text-center mono text-white/40" data-testid="directory-empty">NO PROFILES MATCH</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
        {entries.map((e, i) => {
          const card = (
            <div className="h-full border border-white/10 bg-[#0C0C0C] rounded-lg p-5 hover:border-kinetic/60 transition group">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 border border-white/10 shrink-0">
                  {e.picture ? <img src={imgUrl(e.picture)} alt={e.name} className="w-full h-full object-cover" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                    : <div className="w-full h-full flex items-center justify-center font-sans font-bold text-white/60">{e.name?.[0] || "?"}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-sans font-bold flex items-center gap-1 truncate">{e.name}{e.verified && <BadgeCheck className="w-4 h-4 text-kinetic shrink-0" />}</div>
                  <div className="mono text-white/45 mt-0.5 truncate">{e.headline || (e.category ? e.category.toUpperCase() : "CREATOR")}</div>
                </div>
                {e.slug && <ExternalLink className="w-4 h-4 text-white/25 group-hover:text-kinetic transition shrink-0" />}
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {e.category && <span className="mono text-kinetic border border-kinetic/30 bg-kinetic/5 px-2 py-0.5 rounded-sm">{e.category.toUpperCase()}</span>}
                {e.location && <span className="mono text-white/45 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                {e.rate && <span className="mono text-white/60">{e.rate}</span>}
              </div>
              {Array.isArray(e.skills) && e.skills.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {e.skills.slice(0, 4).map((s, si) => <span key={si} className="mono text-white/50 border border-white/10 px-1.5 py-0.5 rounded-sm">{s}</span>)}
                </div>
              )}
            </div>
          );
          return e.slug
            ? <Link key={i} to={`/u/${e.slug}`} target="_blank" data-testid={`directory-entry-${i}`}>{card}</Link>
            : <Link key={i} to="/app/network" data-testid={`directory-entry-${i}`}>{card}</Link>;
        })}
      </div>
    </div>
  );
}
